import React, { useState } from 'react';
import { Box, Typography, IconButton, Tooltip, Popover, TextField, InputAdornment } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import HistoryIcon from '@mui/icons-material/History';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import ShareIcon from '@mui/icons-material/Share';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { CloseOutlined, DownloadOutlined } from '@ant-design/icons';

const ChatHeader = ({
  title = 'Chat',
  showReportChip = false,
  onClickReportChip = null,
  messagesCount = 0,
  onHistory,
  onNewChat,
  onDownload,
  onToggleFullscreen,
  onClose,
  hideClose = false,
  threadId = null,
  isPublic = false,
  shareUrl = null,
  onShare = null
}) => {
  const [shareAnchorEl, setShareAnchorEl] = useState(null);
  const [copied, setCopied] = useState(false);

  const handleShareClick = (event) => {
    if (onShare) {
      onShare();
    } else if (isPublic && shareUrl) {
      setShareAnchorEl(event.currentTarget);
    }
  };

  const handleCloseSharePopover = () => {
    setShareAnchorEl(null);
    setCopied(false);
  };

  const handleCopyUrl = () => {
    if (shareUrl) {
      // shareUrl from backend is relative: "/shared-reports/<thread_id>"
      // Prepend "/agentic" to get the full path
      const fullPath = `/agentic${shareUrl}`;
      const fullUrl = `${window.location.origin}${fullPath}`;
      navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const sharePopoverOpen = Boolean(shareAnchorEl);

  return (
    <Box sx={{ py: 0, px: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Typography variant="h5">{title}</Typography>
        {showReportChip && (
          <Tooltip title="Report context is active">
            <IconButton
              size="small"
              aria-label="Report context active"
              onClick={onClickReportChip || undefined}
              sx={{ width: 10, height: 10, p: 0, borderRadius: '50%', bgcolor: 'success.light', '&:hover': { bgcolor: 'success.main' } }}
            />
          </Tooltip>
        )}
      </Box>
      <Box>
        <Tooltip title="New chat">
          <IconButton onClick={onNewChat} sx={{ mr: 1 }}>
            <AddIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="History">
          <IconButton onClick={onHistory} sx={{ mr: 1 }}>
            <HistoryIcon />
          </IconButton>
        </Tooltip>
        {messagesCount > 0 && (
          <Tooltip title="Download chat (.docx)">
            <IconButton onClick={onDownload} sx={{ mr: 1 }}>
              <DownloadOutlined />
            </IconButton>
          </Tooltip>
        )}
        {threadId && (
          <Tooltip title={isPublic ? 'Share link' : 'Share this chat'}>
            <IconButton onClick={handleShareClick} sx={{ mr: 1 }} color={isPublic ? 'primary' : 'default'}>
              <ShareIcon />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Fullscreen">
          <IconButton onClick={onToggleFullscreen} sx={{ mr: 1 }}>
            <FullscreenIcon />
          </IconButton>
        </Tooltip>
        {!hideClose && (
          <IconButton onClick={onClose}>
            <CloseOutlined />
          </IconButton>
        )}
      </Box>
      {isPublic && shareUrl && (
        <Popover
          open={sharePopoverOpen}
          anchorEl={shareAnchorEl}
          onClose={handleCloseSharePopover}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right'
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right'
          }}
        >
          <Box sx={{ p: 2, minWidth: 300 }}>
            <Typography variant="subtitle2" gutterBottom>
              Share this chat
            </Typography>
            <TextField
              fullWidth
              size="small"
              value={`${window.location.origin}/agentic${shareUrl}`}
              readOnly
              sx={{ mt: 1 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={handleCopyUrl} edge="end">
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            {copied && (
              <Typography variant="caption" color="success.main" sx={{ mt: 0.5, display: 'block' }}>
                Link copied!
              </Typography>
            )}
          </Box>
        </Popover>
      )}
    </Box>
  );
};

export default ChatHeader;
